@if (isRepresentationActive()) {
	<div style="display: flex; justify-content: center">
		<lvr-hint-banner [message]="'client.create.representation_mode_description' | transloco"></lvr-hint-banner>
	</div>
}

<section role="region" aria-labelledby="client-create-heading">
	<h2 id="client-create-heading" lvrPadding [padHorizontal]="32" [padVertical]="0">
		{{ 'client.create_client.title' | transloco }}
	</h2>

	<form
		class="form-container"
		[formGroup]="form"
		autocomplete="on"
		lvrAutoSave="client"
		[entityId]="draftId"
		[autoLoad]="false"
	>
		<section class="form-grid">
			<!-- Last Name -->
			@let nameCtrl = form.get('name');
			<lvr-field [label]="'base.field.last_name' | transloco | appendAsterisk">
				<mat-form-field lvrInput [lvrError]="hasError(nameCtrl, 'required', 'pattern')">
					<input
						matInput
						formControlName="name"
						placeholder="Musterfrau"
						aria-required="true"
						[errorStateMatcher]="matcher"
					/>
					@if (hasError(nameCtrl, 'required')) {
						<mat-error>{{ 'base.form.error.last_name_required' | transloco }}</mat-error>
					}
					@if (hasError(nameCtrl, 'pattern')) {
						<mat-error>{{ 'base.form.error.last_name_invalid' | transloco }}</mat-error>
					}
				</mat-form-field>
			</lvr-field>

			<!-- First Name -->
			@let firstNameCtrl = form.get('firstName');
			<lvr-field [label]="'base.field.first_name' | transloco">
				<mat-form-field lvrInput [lvrError]="hasError(firstNameCtrl, 'pattern')">
					<input matInput formControlName="firstName" placeholder="Megan" [errorStateMatcher]="matcher" />
					@if (hasError(firstNameCtrl, 'pattern')) {
						<mat-error>{{ 'base.form.error.first_name_invalid' | transloco }}</mat-error>
					}
				</mat-form-field>
			</lvr-field>

			<!-- Birth Name -->
			@let birthNameCtrl = form.get('birthName');
			<lvr-field [label]="'base.field.past_name' | transloco">
				<mat-form-field lvrInput [lvrError]="hasError(birthNameCtrl, 'pattern')">
					<input matInput formControlName="birthName" placeholder="Müller" [errorStateMatcher]="matcher" />
					@if (hasError(birthNameCtrl, 'pattern')) {
						<mat-error>{{ 'base.form.error.past_name_invalid' | transloco }}</mat-error>
					}
				</mat-form-field>
			</lvr-field>

			<!-- Date of Birth -->
			@let dateOfBirthCtrl = form.get('dateOfBirth');
			<lvr-field [label]="'base.field.date_of_birth' | transloco">
				<mat-form-field
					lvrInput
					[class]="showBirthdayWarning() ? 'lvr-warning' : ''"
					[lvrError]="hasError(dateOfBirthCtrl, 'matDatepickerMin', 'matDatepickerParse')"
				>
					<input
						matInput
						[matDatepicker]="picker"
						formControlName="dateOfBirth"
						placeholder="01.07.2025"
						[errorStateMatcher]="matcher"
						[min]="minDate"
						[max]="maxDate"
					/>
					@if (hasError(dateOfBirthCtrl, 'matDatepickerParse')) {
						<mat-error>{{ 'base.form.error.date_format_invalid' | transloco: { format: dateFormatHint } }}</mat-error>
					} @else if (hasError(dateOfBirthCtrl, 'matDatepickerMin')) {
						<mat-error>{{ 'base.form.error.date_min_error' | transloco: { minDate: minDateFormatted } }}</mat-error>
					} @else if (showBirthdayWarning()) {
						<mat-hint class="lvr-warning">{{ 'base.field.birth_warning' | transloco: birthDateWarningRange }}</mat-hint>
					} @else {
						<mat-hint>{{ dateFormatHint }}</mat-hint>
					}
					<mat-datepicker-toggle matIconSuffix [for]="picker"></mat-datepicker-toggle>
					<mat-datepicker #picker></mat-datepicker>
				</mat-form-field>
			</lvr-field>

			<!-- Gender -->
			<lvr-field [label]="'base.field.gender' | transloco">
				<mat-form-field lvrInput [attr.aria-label]="'base.field.gender' | transloco">
					<mat-select
						formControlName="gender"
						[placeholder]="'base.field.select_please' | transloco"
						[compareWith]="compareById"
						aria-required="true"
					>
						@for (option of availableGenders(); track option.id) {
							<mat-option
								[value]="option"
								matTooltipClass="info-tooltip"
								[matTooltip]="
									option.salutation === 'divers u.a.' ? ('client.create_client.gender_hint_divers' | transloco) : null
								"
							>
								{{ option.salutation }}
							</mat-option>
						}
					</mat-select>
				</mat-form-field>
			</lvr-field>

			<!-- Title -->
			@let titleCtrl = form.get('title');
			<lvr-field [label]="'base.field.title' | transloco">
				<mat-form-field lvrInput [lvrError]="hasError(titleCtrl, 'pattern')">
					<input matInput formControlName="title" placeholder="Dr." [errorStateMatcher]="matcher" />
					@if (hasError(titleCtrl, 'pattern')) {
						<mat-error>{{ 'base.form.error.title_invalid' | transloco }}</mat-error>
					}
				</mat-form-field>
			</lvr-field>

			<!-- Address -->
			<lvr-address-completion class="address-completion" formControlName="address"></lvr-address-completion>

			<!-- residence District -->
			<lvr-field [label]="'base.field.residence_district' | transloco">
				<lvr-searchable-select
					formControlName="residenceDistrict"
					[options]="availableResidenceDistrict()"
					[placeholder]="'base.field.select_please' | transloco"
					[searchPlaceholder]="'base.field.search_placeholder' | transloco"
					[noResultsText]="'base.field.no_results' | transloco"
					[ariaLabel]="'base.field.residence_district' | transloco"
					[required]="false"
				></lvr-searchable-select>
			</lvr-field>

			<!-- Spacer to balance height  -->
			<div class="spacer-cell" aria-hidden="true"></div>

			<!-- Communication section -->
			@for (item of communicationWayCountArrayKeys(); track $index) {
				<h3 lvrPadding class="additional-communication-heading" [padVertical]="0" [padHorizontal]="8">
					<span>{{ 'base.field.contact_point.title' | transloco }}</span>
					<button matIconButton class="btn btn-tertiary" (click)="removeCommunicationWay($index)">
						<mat-icon class="material-symbols-outlined" aria-hidden="true">delete</mat-icon>
					</button>
				</h3>
				<lvr-contact-point
					[contactPointCategories]="contactPointCategories()"
					class="address-completion"
					(values)="updateValues($event, $index)"
				></lvr-contact-point>
			}

			<!-- Spacer to balance height  -->
			<div class="spacer-cell" aria-hidden="true"></div>

			<!-- Add additional button -->
			<lvr-button
				lvrPadding
				[padHorizontal]="32"
				[padVertical]="0"
				variant="primary"
				[label]="'base.field.contact_point.title' | transloco"
				[ariaLabel]="'base.field.contact_point.add_communication' | transloco"
				[type]="'button'"
				(clicked)="addCommunicationWay()"
				icon="add"
			></lvr-button>

			<lvr-field [label]="'base.field.social_security_number' | transloco">
				<mat-form-field lvrInput>
					<input matInput formControlName="numberSV" placeholder="123-45-6789" />
				</mat-form-field>
			</lvr-field>

			<!-- Spacer to balance height  -->
			<div class="spacer-cell" aria-hidden="true"></div>

			<!-- BA/AfG Client Number -->
			<lvr-field [label]="'base.field.client_number_afg' | transloco">
				<mat-form-field lvrInput>
					<input matInput formControlName="numberBA" placeholder="123-45-6789" />
				</mat-form-field>
			</lvr-field>

			<!-- Health Insurance Number -->
			<lvr-field [label]="'base.field.health_insurance_number' | transloco">
				<mat-form-field lvrInput>
					<input matInput formControlName="numberKV" placeholder="123-45-6789" />
				</mat-form-field>
			</lvr-field>

			<p class="required-hint">{{ 'base.form.required' | transloco }}</p>

			<lvr-default-actions
				[disableSave]="form.invalid || !canCreateClientsPermission()"
				[showLoading]="showLoading()"
				(save)="createClient()"
				(cancelSave)="cancel()"
			>
				<lvr-restore-actions entityType="client" [entityId]="draftId"></lvr-restore-actions>
			</lvr-default-actions>
		</section>
	</form>
</section>
